import * as Tone from 'tone';
import { putSample, deleteSample } from '../core/sample-store.js';

// Drum kit module. Each drum becomes its own mixer channel + instrument +
// channel-rack lane, so a beat is just toggling steps on the lane.
// Pooled voices guard against "strictly increasing time" errors when a drum
// has a long release (open hat, crash) that overlaps the next 16th-note hit.

const KIT = [
    {
        id: 'kick', name: 'Kick',
        ctor: Tone.MembraneSynth,
        opts: { pitchDecay: 0.05, octaves: 6, oscillator: { type: 'sine' },
                envelope: { attack: 0.001, decay: 0.4, sustain: 0.01, release: 1.4 } },
        trigger: 'pitched', note: 'C1', dur: '8n', vol: -4,
    },
    {
        id: 'snare', name: 'Snare',
        ctor: Tone.NoiseSynth,
        opts: { noise: { type: 'white' }, envelope: { attack: 0.001, decay: 0.18, sustain: 0 } },
        trigger: 'noise', dur: '16n', vol: -10,
    },
    {
        id: 'hatClosed', name: 'Hat (closed)',
        ctor: Tone.MetalSynth,
        opts: { frequency: 250, envelope: { attack: 0.001, decay: 0.05, release: 0.01 },
                harmonicity: 5.1, modulationIndex: 32, resonance: 4000, octaves: 1.5 },
        trigger: 'metal', dur: '32n', vel: 0.3, vol: -12,
    },
    {
        id: 'hatOpen', name: 'Hat (open)',
        ctor: Tone.MetalSynth,
        opts: { frequency: 250, envelope: { attack: 0.001, decay: 0.4, release: 0.2 },
                harmonicity: 5.1, modulationIndex: 32, resonance: 4000, octaves: 1.5 },
        trigger: 'metal', dur: '32n', vel: 0.3, vol: -14,
    },
    {
        id: 'clap', name: 'Clap',
        ctor: Tone.NoiseSynth,
        opts: { noise: { type: 'pink' }, envelope: { attack: 0.005, decay: 0.2, sustain: 0 } },
        trigger: 'noise', dur: '16n', vol: -14,
    },
    {
        id: 'tom', name: 'Tom',
        ctor: Tone.MembraneSynth,
        opts: { pitchDecay: 0.08, octaves: 4, oscillator: { type: 'sine' },
                envelope: { attack: 0.001, decay: 0.5, sustain: 0.01, release: 1.0 } },
        trigger: 'pitched', note: 'A1', dur: '8n', vol: -10,
    },
    {
        id: 'rim', name: 'Rim',
        ctor: Tone.MetalSynth,
        opts: { frequency: 600, envelope: { attack: 0.001, decay: 0.02, release: 0.01 },
                harmonicity: 8, modulationIndex: 16, resonance: 6000, octaves: 0.5 },
        trigger: 'metal', dur: '32n', vel: 0.4, vol: -12,
    },
    {
        id: 'crash', name: 'Crash',
        ctor: Tone.MetalSynth,
        opts: { frequency: 200, envelope: { attack: 0.001, decay: 1.4, release: 1.0 },
                harmonicity: 8.5, modulationIndex: 64, resonance: 8000, octaves: 2 },
        trigger: 'metal', dur: '4n', vel: 0.3, vol: -18,
    },
    {
        id: 'ride', name: 'Ride',
        ctor: Tone.MetalSynth,
        opts: { frequency: 320, envelope: { attack: 0.001, decay: 0.6, release: 0.4 },
                harmonicity: 5.5, modulationIndex: 40, resonance: 5000, octaves: 1.2 },
        trigger: 'metal', dur: '8n', vel: 0.3, vol: -14,
    },
];

function makePool(Ctor, opts, size = 6) {
    const voices = Array.from({ length: size }, () => new Ctor(opts));
    let i = 0;
    return {
        connect(dest) { voices.forEach(v => v.connect(dest)); return this; },
        trigger(...args) {
            const v = voices[i];
            i = (i + 1) % size;
            try { v.triggerAttackRelease(...args); } catch (_) { /* ignore overflow */ }
        },
    };
}

export default {
    id: 'drums',
    name: 'Drums',
    register(ctx) {
        for (const d of KIT) {
            const channel = ctx.mixer.addChannel({
                id: d.id, name: d.name, color: '#4ec9b0',
            });
            channel.setVolume(d.vol);
            const pool = makePool(d.ctor, d.opts).connect(channel.input);

            // Per-drum sample state. When a sample is loaded, play() routes
            // through the Tone.Player instead of the synthesized voice pool.
            // Reload replaces the player; clear reverts to the synth fallback.
            let player = null;
            let sampleName = null;

            ctx.instruments.register({
                id: d.id,
                name: d.name,
                kind: 'drum',
                channelId: d.id,
                play(_note, time, _duration, velocity = 0.85) {
                    if (player && player.loaded) {
                        try {
                            player.volume.value = Tone.gainToDb(Math.max(0.01, velocity));
                            // Always restart from 0 — drum samples should retrigger
                            // cleanly rather than doubling up with the previous hit.
                            if (player.state === 'started') player.stop(time);
                            player.start(time);
                        } catch (_) { /* retrigger race — safe to drop */ }
                        return;
                    }
                    // All Tone synths in this kit use the 4-arg form
                    // (carrier, duration, time, velocity). NoiseSynth is the
                    // exception — it has no carrier pitch.
                    //   pitched  → MembraneSynth: pass `d.note`
                    //   metal    → MetalSynth: pass the kit's center frequency
                    //   noise    → NoiseSynth: 3-arg form
                    // Cell velocity multiplies the kit's per-drum scalar so
                    // MetalSynth (which is harsh at vel=1) stays reasonable.
                    if (d.trigger === 'pitched') {
                        pool.trigger(d.note, d.dur, time, velocity);
                    } else if (d.trigger === 'noise') {
                        pool.trigger(d.dur, time, velocity);
                    } else {
                        const v = velocity * (d.vel ?? 0.5);
                        pool.trigger(d.opts.frequency, d.dur, time, v);
                    }
                },
                async loadSample(source, displayName, opts = {}) {
                    // Accepts a File, Blob, or AudioBuffer. `opts.persist=false`
                    // skips writing to IndexedDB — used during boot hydration
                    // so we don't re-store the same blob we just read.
                    const isFile = source instanceof File;
                    const name = displayName ?? (isFile ? source.name : 'sample');
                    let arrayBuffer;
                    if (source instanceof Blob) {
                        arrayBuffer = await source.arrayBuffer();
                    } else if (source instanceof ArrayBuffer) {
                        arrayBuffer = source;
                    } else {
                        throw new Error('loadSample: unsupported source type');
                    }
                    const audioBuffer = await Tone.context.decodeAudioData(arrayBuffer.slice(0));
                    if (player) {
                        try { player.disconnect(); player.dispose(); } catch (_) {}
                    }
                    player = new Tone.Player();
                    player.buffer = new Tone.ToneAudioBuffer(audioBuffer);
                    player.connect(channel.input);
                    sampleName = name;
                    if (opts.persist !== false && source instanceof Blob) {
                        try { await putSample(`drum:${d.id}`, source, name); } catch (_) {}
                    }
                    ctx.bus.emit('sample:loaded', { instrumentId: d.id, name: sampleName });
                },
                clearSample() {
                    if (player) {
                        try { player.disconnect(); player.dispose(); } catch (_) {}
                    }
                    player = null;
                    sampleName = null;
                    deleteSample(`drum:${d.id}`).catch(() => {});
                    ctx.bus.emit('sample:cleared', { instrumentId: d.id });
                },
                getSampleName: () => sampleName,
                hasSample: () => !!player,
            });
            ctx.lanes.add({
                id: d.id,
                name: d.name,
                instrumentId: d.id,
                kind: 'drum',
                color: '#4ec9b0',
                acceptsSample: true,
            });
        }
    },
};
